import { useCallback, useContext, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { QuantAnalysisContext, QuantAnalysisVersionContext } from '@trinity/chat-components';
import { useModal, type BaseModalProps } from '@trinity/chat-components/hooks/useModal.js';
import { useInvalidateQuantFolders } from '@trinity/chat-components/hooks/useInvalidateQuantFolders';
import { Body } from '@trinity/chat-components/components/Modal/Body.js';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet.js';
import { Header } from '@trinity/chat-components/components/Modal/Header.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator.js';
import { ButtonOutlined } from '@trinity/chat-components/components/presentation/ButtonOutlined.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { Input } from '@/components/presentation/Input';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';
import styles from './style/Modal.RenameAnalysisItem.module.css';

type Props = BaseModalProps & {
  projectId: number;
  quantAnalysisId: number;
  name: string;
  folderId: number;
};

export const RenameQuantFolderModal = (props: Props) => {
  const [input, setInput] = useState(props.name);

  const quantAnalysis = useContext(QuantAnalysisContext);
  const quantVersion = useContext(QuantAnalysisVersionContext);
  const projectDetails = useContext(ProjectDetailsContext);
  const apiClient = useChatApiClient();
  const projectId = projectDetails.query.data?.project?.id;
  const invalidateQuantFolders = useInvalidateQuantFolders();

  const renameMutation = useMutation({
    mutationFn: () => {
      return apiClient.quantAnalysis.renameQuantFolder({
        quantAnalysisId: quantAnalysis.id,
        folderId: props.folderId,
        newName: input,
        projectId: projectId,
      });
    },
    onSuccess: () => {
      invalidateQuantFolders(quantAnalysis.id, projectId, quantVersion?.id);
      props.onClose();
    },
  });

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  }, []);

  const { mutateAsync: submitAsync } = renameMutation;
  const handleSubmit = useCallback(() => {
    submitAsync();
  }, [submitAsync]);

  const canSubmit = useMemo(() => {
    return input.trim().length > 0;
  }, [input]);

  return (
    <Modal
      disableEscapeClose={renameMutation.isPending}
      disableOverlayClick={renameMutation.isPending}
      hideCloseIcon
      open={props.open}
      onClose={props.onClose}
    >
      <Body>
        <Header>{`Rename "${props.name}"`}</Header>

        <div className={styles.input}>
          <Input autoFocus onChange={handleInputChange} placeholder="Enter a new name" value={input} />
        </div>
      </Body>
      <ButtonSet>
        <ButtonOutlined
          borderWidth="thin"
          disabled={renameMutation.isPending}
          color="silver"
          onClick={props.onClose}
        >
          Cancel
        </ButtonOutlined>
        <ButtonActivityIndicator
          color="destructive"
          indicatorSize={12}
          disabled={!canSubmit}
          implicitDisable={renameMutation.isPending}
          loading={renameMutation.isPending}
          onClick={handleSubmit}
        >
          Submit
        </ButtonActivityIndicator>
      </ButtonSet>
    </Modal>
  );
};

export const useRenameQuantFolderModal = () => useModal(RenameQuantFolderModal);
